<script setup lang="ts">
import ColumnMapping from '@/components/ColumnMapping.vue';
import MultipleValues from '@/components/record/MultipleValues.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { fieldKeyOf } from '@/components/record/fields/fieldKind';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * The fields a layout pins under a record's heading, drawn as chips. One
 * record and the combined record of several draw them here alike.
 *
 * With labels hidden, a chip shows only its value, and hovering it names the
 * field.
 *
 * On the combined record, a chip whose field the selected records differ on
 * reads "Multiple values" instead of a value. It keeps its label, its tooltip
 * and its name for screen readers.
 */
const props = withDefaults(
    defineProps<{
        columns: TableColumnStaticProps[];
        row: Record<string, unknown>;
        hideLabels: boolean;
        /** The top-level keys of the row on which the selected records differ. */
        multipleKeys?: string[];
    }>(),
    { multipleKeys: () => [] },
);

function hasMultipleValues(column: TableColumnStaticProps): boolean {
    return props.multipleKeys.includes(fieldKeyOf(column));
}
</script>

<template>
  <!-- Chips show their whole value, on one line. The value's own renderer, such
       as a related record's link, truncates in a table cell, so that is undone
       here. A chip grows to fit its value, up to the width of the row. Only a
       value longer than the whole row is cut, at the row's edge. -->
  <!-- `-mx-3` matches each chip's `px-3`, so a chip's text starts on the same
       edge as the title and the fields below. The rounded fill hangs into the
       page margin instead. Without it the chips looked shifted right. -->
  <ul v-if="columns.length > 0" class="-mx-3 flex flex-wrap gap-2" aria-label="Key fields" data-testid="record-chips">
    <li v-for="column in columns" :key="column.id" class="max-w-full" :data-testid="`record-chip-${column.id}`">
      <Tooltip v-if="hideLabels">
        <TooltipTrigger as-child>
          <span tabindex="0" :aria-label="column.header" class="inline-flex h-7 max-w-full items-center overflow-hidden rounded-full bg-muted px-3 text-xs [&_a]:no-underline">
            <span class="min-w-0 text-foreground whitespace-nowrap [&_*]:whitespace-nowrap [&_.truncate]:overflow-visible [&_.truncate]:text-clip" data-testid="record-chip-value">
              <MultipleValues v-if="hasMultipleValues(column)" test-id="record-chip-multiple" />
              <ColumnMapping v-else :field="column.key" :data-type="column.dataType" :data="row" />
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent data-testid="record-chip-tooltip">{{ column.header }}</TooltipContent>
      </Tooltip>
      <span v-else class="inline-flex h-7 max-w-full items-center gap-1.5 overflow-hidden rounded-full bg-muted px-3 text-xs [&_a]:no-underline">
        <span class="shrink-0 text-muted-foreground" data-testid="record-chip-label">{{ column.header }}</span>
        <span class="min-w-0 text-foreground whitespace-nowrap [&_*]:whitespace-nowrap [&_.truncate]:overflow-visible [&_.truncate]:text-clip" data-testid="record-chip-value">
          <MultipleValues v-if="hasMultipleValues(column)" test-id="record-chip-multiple" />
          <ColumnMapping v-else :field="column.key" :data-type="column.dataType" :data="row" />
        </span>
      </span>
    </li>
  </ul>
</template>
